import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { ArrowLeft, Rocket, ExternalLink, RefreshCw, Facebook, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function PublishMeta() {
  const { renderId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [asset, setAsset] = useState(null);
  const [adAccounts, setAdAccounts] = useState([]);
  const [pages, setPages] = useState([]);
  const [connected, setConnected] = useState(false);

  const [form, setForm] = useState({
    ad_account_id: "",
    page_id: "",
    campaign_name: "",
    headline: "",
    message: "",
    landing_url: "",
    daily_budget: 5,    // dollars/day, converted to cents on submit
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const accRes = await api.get("/social/accounts");
      const isConnected = accRes.data.connections.some((c) => c.platform === "meta" && !c.is_mock);
      setConnected(isConnected);

      // Fetch the render job by id; ignore if not completed yet
      const jobRes = await api.get(`/renders/${renderId}`).catch(() => null);
      const job = jobRes?.data;
      if (job && job.status === "completed") {
        // Resolve the variation title for nicer defaults
        let title = "";
        if (job.variation_id) {
          const v = await api.get(`/variations/${job.variation_id}`).catch(() => null);
          title = v?.data?.title || v?.data?.name || "";
        }
        setAsset({
          id: job.id,                   // we'll publish using render-job id; backend resolves the asset
          render_job_id: job.id,
          output_url: job.output_url,
          size_bytes: 0,
          variation_title: title,
        });
        setForm((f) => ({
          ...f,
          campaign_name: `Codira: ${title || "Video Ad"}`.slice(0, 80),
          headline: (title || "Built with Codira").slice(0, 90),
        }));
      }

      if (isConnected) {
        const aa = await api.get("/social/meta/adaccounts").catch(() => ({ data: { ad_accounts: [] } }));
        setAdAccounts(aa.data.ad_accounts || []);
        setForm((f) => ({
          ...f,
          ad_account_id: f.ad_account_id || (aa.data.ad_accounts?.[0]?.id || ""),
        }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load");
    } finally { setLoading(false); }
  };

  // Whenever ad account changes, re-fetch pages owned by THAT business
  useEffect(() => {
    if (!form.ad_account_id) { setPages([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const pg = await api.get("/social/meta/pages", { params: { ad_account_id: form.ad_account_id } });
        if (cancelled) return;
        const list = pg.data.pages || [];
        setPages(list);
        // Reset page selection if the currently-picked page isn't in the new list
        setForm((f) => {
          if (f.page_id && list.some((p) => p.id === f.page_id)) return f;
          return { ...f, page_id: list[0]?.id || "" };
        });
      } catch (e) {
        if (!cancelled) setPages([]);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.ad_account_id]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [renderId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!asset?.id) return toast.error("Render not found");
    if (!form.ad_account_id) return toast.error("Pick an ad account");
    if (!form.page_id) return toast.error("Pick a Facebook Page");
    setSubmitting(true);
    try {
      const { data } = await api.post("/social/meta/publish", {
        rendered_asset_id: asset.id,
        ad_account_id: form.ad_account_id,
        page_id: form.page_id,
        campaign_name: form.campaign_name,
        headline: form.headline,
        message: form.message,
        landing_url: form.landing_url || undefined,
        daily_budget_cents: Math.max(100, Math.round(Number(form.daily_budget) * 100)),
      });
      setResult(data);
      if (data.needs_manual_finish) {
        toast.success("Uploaded to Meta — finish in Ads Manager");
      } else {
        toast.success("Published to Meta — paused for review");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="p-12 text-center text-sm text-gray-500"><RefreshCw className="inline h-4 w-4 animate-spin mr-2" /> Loading…</div>;
  }

  if (!connected) {
    return (
      <div className="p-8 max-w-2xl">
        <Link to="/renders" className="text-xs text-gray-500 hover:text-brand inline-flex items-center gap-1 mb-2">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to renders
        </Link>
        <h1 className="font-display text-4xl text-gray-900">Publish to Meta</h1>
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-900 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Meta isn't connected yet</p>
            <p className="mt-1 text-amber-800">Connect your Meta Business account first so Codira can publish ads on your behalf.</p>
            <Link to="/social" className="btn-brand inline-flex items-center gap-2 mt-3 px-3 py-1.5 text-xs">
              <Facebook className="h-3.5 w-3.5" /> Go to Channels
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="p-8 max-w-2xl">
        <Link to="/renders" className="text-xs text-gray-500 hover:text-brand inline-flex items-center gap-1 mb-2">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to renders
        </Link>
        <h1 className="font-display text-4xl text-gray-900">Publish to Meta</h1>
        <p className="text-sm text-gray-500 mt-2">Render not found or not yet completed.</p>
      </div>
    );
  }

  if (result) {
    const draftMode = result.needs_manual_finish;
    return (
      <div className="p-8 max-w-3xl">
        <Link to="/renders" className="text-xs text-gray-500 hover:text-brand inline-flex items-center gap-1 mb-2">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to renders
        </Link>
        <div className="bg-white border border-gray-200 rounded-2xl p-8 mt-3">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${
            draftMode ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>
            <Rocket className="h-3.5 w-3.5" />
            {draftMode ? "Draft ready — one click to finish in Meta" : "Ad created (PAUSED)"}
          </div>
          <h1 className="font-display text-3xl text-gray-900 mt-4">
            {draftMode
              ? "Almost there — finish in Meta Ads Manager"
              : "Your ad is in Meta — ready for review"}
          </h1>
          <p className="text-sm text-gray-600 mt-2">
            {draftMode
              ? "Your video, headline, body, and Ad Set are all uploaded to Meta. Because Codira is in Meta's App Review process, the very last step — creating the Ad — has to be done with one click inside Meta Ads Manager. Takes 30 seconds."
              : <>We created a Campaign, Ad Set, and Ad in your selected ad account with <strong>status: PAUSED</strong>. Nothing has spent money yet. Review it in Ads Manager and click "Publish" inside Meta when you're ready to go live.</>}
          </p>

          {draftMode && (
            <div className="mt-5 bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
              <p className="font-semibold mb-1">How to finish:</p>
              <ol className="list-decimal pl-5 space-y-1 text-amber-800">
                <li>Click <strong>"Open in Meta Ads Manager"</strong> below.</li>
                <li>Your Ad Set "<span className="font-mono">{(result.publish?.rendered_asset_id || "").slice(0, 6)}…</span>" is selected.</li>
                <li>Click <strong>"Create Ad"</strong> → pick the existing video/creative from your library → save.</li>
                <li>Leave it PAUSED. Activate when you're ready.</li>
              </ol>
            </div>
          )}

          <div className="mt-6 space-y-1 text-xs font-mono text-gray-600 bg-gray-50 rounded-lg p-4">
            <p>Campaign: <span className="text-gray-900">{result.campaign_id}</span></p>
            <p>Ad Set: <span className="text-gray-900">{result.adset_id}</span></p>
            {result.ad_id && <p>Ad: <span className="text-gray-900">{result.ad_id}</span></p>}
            <p>Creative: <span className="text-gray-900">{result.creative_id}</span></p>
            <p>Video: <span className="text-gray-900">{result.video_id}</span></p>
          </div>

          <a href={result.review_url} target="_blank" rel="noreferrer"
             className="btn-brand inline-flex items-center gap-2 mt-6 px-4 py-2 text-sm">
            <ExternalLink className="h-4 w-4" />
            {draftMode ? "Open in Meta Ads Manager" : "Review in Meta Ads Manager"}
          </a>
          <button onClick={() => navigate("/renders")}
                  className="ml-3 inline-flex items-center gap-2 px-4 py-2 text-sm border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50">
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl">
      <Link to="/renders" className="text-xs text-gray-500 hover:text-brand inline-flex items-center gap-1 mb-2">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to renders
      </Link>
      <h1 className="font-display text-4xl text-gray-900">Publish to Meta</h1>
      <p className="text-sm text-gray-500 mt-2 max-w-2xl">
        Push this rendered ad to Facebook + Instagram. We'll create a campaign in your selected ad account.
        Ads are created <strong>PAUSED</strong> — nothing spends until you approve it in Meta Ads Manager.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr,1.4fr] gap-8 mt-8 items-start">
        {/* Preview */}
        <div className="bg-gray-900 rounded-xl overflow-hidden">
          <video src={fileUrl(`/api/renders/${asset.render_job_id}/file`)}
                 controls autoPlay loop muted className="w-full" />
          <div className="p-4 text-xs text-gray-300 flex items-center justify-between">
            <span>{asset.variation_title || "Untitled variation"}</span>
            <span>{(asset.size_bytes / 1024).toFixed(0)} KB</span>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="text-xs uppercase tracking-wider font-medium text-gray-500">Ad account</label>
            <select required value={form.ad_account_id}
                    onChange={(e) => setForm({ ...form, ad_account_id: e.target.value })}
                    data-testid="publish-ad-account"
                    className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:border-brand focus:ring-1 focus:ring-brand outline-none bg-white">
              <option value="">Select…</option>
              {adAccounts.map((a) => (
                <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>
              ))}
            </select>
            {adAccounts.length === 0 && (
              <p className="text-xs text-amber-700 mt-1">No ad accounts found — create one in Meta Business Manager.</p>
            )}
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-medium text-gray-500">Facebook Page</label>
            <select required value={form.page_id}
                    onChange={(e) => setForm({ ...form, page_id: e.target.value })}
                    data-testid="publish-page"
                    className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:border-brand focus:ring-1 focus:ring-brand outline-none bg-white">
              <option value="">Select…</option>
              {pages.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}{p.instagram_business_account_id ? " — IG linked" : ""}
                </option>
              ))}
            </select>
            {pages.length === 0 && (
              <p className="text-xs text-amber-700 mt-1">No Pages found — you need a Facebook Page to attach video ads to.</p>
            )}
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-medium text-gray-500">Campaign name</label>
            <input required maxLength={80} value={form.campaign_name}
                   onChange={(e) => setForm({ ...form, campaign_name: e.target.value })}
                   data-testid="publish-campaign-name"
                   className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:border-brand focus:ring-1 focus:ring-brand outline-none" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-medium text-gray-500">Headline</label>
              <input required maxLength={90} value={form.headline}
                     onChange={(e) => setForm({ ...form, headline: e.target.value })}
                     placeholder="Built with Codira"
                     data-testid="publish-headline"
                     className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:border-brand focus:ring-1 focus:ring-brand outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-medium text-gray-500">Daily budget ($)</label>
              <input required type="number" min={1} step={1} value={form.daily_budget}
                     onChange={(e) => setForm({ ...form, daily_budget: e.target.value })}
                     data-testid="publish-budget"
                     className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:border-brand focus:ring-1 focus:ring-brand outline-none" />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-medium text-gray-500">Body / message</label>
            <textarea rows={3} maxLength={280} value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="A short, scroll-stopping line. Up to 280 characters."
                      data-testid="publish-message"
                      className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:border-brand focus:ring-1 focus:ring-brand outline-none resize-none" />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-medium text-gray-500">Landing URL (CTA destination)</label>
            <input type="url" value={form.landing_url}
                   onChange={(e) => setForm({ ...form, landing_url: e.target.value })}
                   placeholder="https://your-site.com/offer"
                   data-testid="publish-landing"
                   className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:border-brand focus:ring-1 focus:ring-brand outline-none" />
            <p className="text-[10px] text-gray-400 mt-1">Where the "Learn More" CTA points. Defaults to studio.codira.com if left blank.</p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900 flex gap-2">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <p>The ad is created <strong>PAUSED</strong>. Nothing spends until you go to Meta Ads Manager and turn it on.</p>
          </div>

          <button type="submit" disabled={submitting}
                  data-testid="publish-submit"
                  className="btn-brand inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold disabled:opacity-50">
            {submitting
              ? <><RefreshCw className="h-4 w-4 animate-spin" /> Publishing…</>
              : <><Rocket className="h-4 w-4" /> Publish to Meta</>}
          </button>
        </form>
      </div>
    </div>
  );
}
